import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue } from "framer-motion";
import { ArrowDown, ArrowRight, Moon, Sun } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { isEveningNow } from "@/lib/sunset";

const EASE = [0.22, 1, 0.36, 1];

const SKY_BG =
  "https://images.unsplash.com/photo-1513002749550-c59d786b8e6c?auto=format&fit=crop&w=2000&q=80";
const SUNSET_BG =
  "https://images.unsplash.com/photo-1472120435266-53107fd0c44a?auto=format&fit=crop&w=2000&q=80";
const CLOUD_A =
  "https://images.unsplash.com/photo-1501630834273-4b5604d2ee31?auto=format&fit=crop&w=900&q=75";
const CLOUD_B =
  "https://images.unsplash.com/photo-1419833173245-f59e1b93f9ee?auto=format&fit=crop&w=900&q=75";

const CLOUDS = [
  { img: CLOUD_A, top: "6%", width: 720, opacity: 0.75, duration: 160, delay: -40, bob: 10 },
  { img: CLOUD_B, top: "20%", width: 560, opacity: 0.6, duration: 120, delay: -90, bob: 8, flip: true },
  { img: CLOUD_A, top: "52%", width: 640, opacity: 0.65, duration: 140, delay: -110, bob: 9, flip: true },
  { img: CLOUD_B, top: "68%", width: 780, opacity: 0.7, duration: 180, delay: -60, bob: 11 },
];

const MASK = "radial-gradient(ellipse at center, black 38%, transparent 74%)";

const clamp01 = (v) => Math.min(1, Math.max(0, v));
const lerp = (a, b, t) => a + (b - a) * t;
const interp = (p, keys, vals) => {
  if (p <= keys[0]) return vals[0];
  for (let i = 1; i < keys.length; i++) {
    if (p <= keys[i]) {
      const t = (p - keys[i - 1]) / (keys[i] - keys[i - 1]);
      return lerp(vals[i - 1], vals[i], t);
    }
  }
  return vals[vals.length - 1];
};

function Airliner() {
  return (
    <svg viewBox="0 0 240 72" className="h-full w-full" data-testid="hero-airplane-svg">
      <defs>
        <linearGradient id="fuselage" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="55%" stopColor="#f2f6fa" />
          <stop offset="100%" stopColor="#d4dfe9" />
        </linearGradient>
      </defs>
      <path d="M118 26 L152 5 L162 5 L130 27 Z" fill="#c4cfdb" />
      <path d="M34 27 L48 5 L61 5 L54 27 Z" fill="url(#fuselage)" stroke="#aebac8" strokeWidth="1" />
      <path
        d="M16 37 C16 29 30 25 56 24 L172 24 C204 24 226 29 237 36.5 C226 43 204 47 172 47 L56 47 C30 46 16 44 16 37 Z"
        fill="url(#fuselage)"
        stroke="#aebac8"
        strokeWidth="1"
      />
      <path d="M30 44 L9 59 L24 59 L45 45 Z" fill="#dde5ee" stroke="#aebac8" strokeWidth="0.8" />
      <path d="M110 30 L60 65 L80 65 L132 33 Z" fill="#e6edf4" stroke="#aebac8" strokeWidth="1" />
      <ellipse cx="97" cy="53" rx="11" ry="6" fill="#d4dfe9" stroke="#a4b1c0" strokeWidth="1" />
      <ellipse cx="126" cy="50" rx="9" ry="5" fill="#d4dfe9" stroke="#a4b1c0" strokeWidth="0.8" />
      <line
        x1="66"
        y1="31.5"
        x2="205"
        y2="31.5"
        stroke="#0B0C0E"
        strokeWidth="2.6"
        strokeDasharray="2.5 7"
        opacity="0.3"
        strokeLinecap="round"
      />
      <path d="M215 28.5 L228 32.5 L215 34 Z" fill="#0B0C0E" opacity="0.38" />
      <path d="M44 44 L198 44" stroke="#c0cbd8" strokeWidth="1" opacity="0.7" />
    </svg>
  );
}

export default function Hero() {
  const heroRef = useRef(null);
  const oRef = useRef(null);
  const [night, setNight] = useState(() => isEveningNow());
  const nightRef = useRef(false);
  const manualRef = useRef(false);
  const updateFnRef = useRef(() => {});

  const planeX = useMotionValue(-300);
  const planeY = useMotionValue(0);
  const planeRotate = useMotionValue(8);
  const planeScale = useMotionValue(0.6);
  const planeOpacity = useMotionValue(0);
  const titleOpacity = useMotionValue(1);
  const skyY = useMotionValue("0%");
  const glowX = useMotionValue(0);
  const glowY = useMotionValue(0);
  const glowOpacity = useMotionValue(0);

  useEffect(() => {
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
    window.scrollTo(0, 0);
    nightRef.current = night;

    const update = () => {
      const hero = heroRef.current;
      const o = oRef.current;
      if (!hero || !o) return;
      const range = hero.offsetHeight - window.innerHeight;
      const p = range > 0 ? clamp01(window.scrollY / range) : 0;

      // Live measurement of the "o" counter — always accurate while pinned
      const or = o.getBoundingClientRect();
      const ox = or.left + or.width / 2;
      const oy = or.top + or.height * 0.52;
      const w = window.innerWidth;

      const start = -280;
      const end = w + 280;
      const pO = Math.min(Math.max((ox - start) / (end - start), 0.12), 0.85);
      const pCross = 0.02 + pO * 0.96;

      planeX.set(lerp(start, end, clamp01((p - 0.02) / 0.96)));
      planeY.set(interp(p, [0.02, pCross * 0.6, pCross, 0.78, 0.98], [oy - 185, oy - 80, oy, oy - 60, oy - 155]));
      planeRotate.set(interp(p, [0.02, pCross * 0.6, pCross, 0.78, 0.98], [8, 3, 0, -4, -10]));
      planeScale.set(interp(p, [0.02, pCross, 0.98], [0.6, 1.05, 0.8]));
      planeOpacity.set(interp(p, [0, 0.03, 0.93, 0.995], [0, 1, 1, 0]));
      titleOpacity.set(interp(p, [0.82, 0.97], [1, 0]));
      skyY.set(`${-6 * p}%`);

      // Landing-light glow washing over the "o" at crossing (evening mode)
      glowX.set(ox);
      glowY.set(oy);
      const proximity = Math.exp(-Math.pow((p - pCross) / 0.07, 2));
      glowOpacity.set(nightRef.current ? proximity * 0.95 : 0);
    };

    updateFnRef.current = update;
    update();
    const t = setTimeout(update, 500);
    if (document.fonts?.ready) document.fonts.ready.then(update);
    // Auto sunset over Naples: re-check the visitor's clock every minute
    // until the visitor overrides manually with the toggle.
    const auto = setInterval(() => {
      if (manualRef.current) return;
      const v = isEveningNow();
      if (v !== nightRef.current) {
        nightRef.current = v;
        setNight(v);
        update();
      }
    }, 60000);
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      clearTimeout(t);
      clearInterval(auto);
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [planeX, planeY, planeRotate, planeScale, planeOpacity, titleOpacity, skyY, glowX, glowY, glowOpacity]);

  const toggleNight = () => {
    manualRef.current = true;
    nightRef.current = !night;
    setNight(!night);
    updateFnRef.current();
  };

  return (
    <section id="hero" ref={heroRef} className="relative h-[230vh]" data-testid="hero-section" data-night={night ? "true" : "false"}>
      {/* Pinned sky scene */}
      <div
        className={`sticky top-0 flex h-screen flex-col items-center justify-center overflow-hidden transition-colors duration-1000 ${
          night ? "bg-[#1a1440]" : "bg-[#79B4E0]"
        }`}
      >
        {/* Day sky */}
        <motion.img
          src={SKY_BG}
          alt=""
          aria-hidden="true"
          className={`sky-pan absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${
            night ? "opacity-0" : "opacity-100"
          }`}
          style={{ y: skyY, scale: 1.06, filter: "saturate(1.15) brightness(1.04)" }}
        />
        {/* Sunset sky */}
        <motion.img
          src={SUNSET_BG}
          alt=""
          aria-hidden="true"
          className={`sky-pan absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${
            night ? "opacity-100" : "opacity-0"
          }`}
          style={{ y: skyY, scale: 1.06, objectPosition: "center top", filter: "saturate(1.2) brightness(0.95)" }}
          data-testid="hero-sunset-sky"
        />
        {/* Day azure tint + horizon blend */}
        <div
          className={`absolute inset-0 transition-opacity duration-1000 ${night ? "opacity-0" : "opacity-100"}`}
          aria-hidden="true"
          style={{
            background:
              "linear-gradient(180deg, rgba(56,130,200,0.28) 0%, rgba(120,180,225,0.10) 35%, rgba(255,255,255,0) 60%, rgba(250,250,252,0.55) 88%, rgba(250,250,252,0.9) 100%)",
          }}
        />
        {/* Evening dusk tint */}
        <div
          className={`absolute inset-0 transition-opacity duration-1000 ${night ? "opacity-100" : "opacity-0"}`}
          aria-hidden="true"
          style={{
            background:
              "linear-gradient(180deg, rgba(20,16,58,0.55) 0%, rgba(52,28,80,0.28) 40%, rgba(20,16,58,0.15) 70%, rgba(10,10,30,0.6) 100%)",
          }}
        />

        {/* Drifting real cumulus patches */}
        <div className="absolute inset-0" aria-hidden="true">
          {CLOUDS.map((c, i) => (
            <div
              key={i}
              className="cloud-real transition-opacity duration-1000"
              style={{
                top: c.top,
                left: 0,
                width: c.width,
                opacity: night ? c.opacity * 0.28 : c.opacity,
                animationDuration: `${c.duration}s`,
                animationDelay: `${c.delay}s`,
                mixBlendMode: "screen",
              }}
            >
              <div style={{ animation: `cloud-bob ${c.bob}s ease-in-out infinite` }}>
                <img
                  src={c.img}
                  alt=""
                  loading="eager"
                  className="h-auto w-full"
                  style={{
                    WebkitMaskImage: MASK,
                    maskImage: MASK,
                    filter: night
                      ? "brightness(0.75) saturate(1.3) hue-rotate(-18deg)"
                      : "brightness(1.1) saturate(0.85)",
                    transform: c.flip ? "scaleX(-1)" : undefined,
                    transition: "filter 1s ease",
                  }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Landing-light glow on the "o" (evening) */}
        <motion.div
          className="pointer-events-none absolute left-0 top-0 z-[15] h-80 w-80 rounded-full"
          style={{
            x: glowX,
            y: glowY,
            opacity: glowOpacity,
            translateX: "-50%",
            translateY: "-50%",
            background:
              "radial-gradient(circle, rgba(255,226,180,0.95) 0%, rgba(255,190,120,0.45) 38%, rgba(255,170,90,0.12) 60%, transparent 72%)",
            mixBlendMode: "screen",
            filter: "blur(6px)",
          }}
          data-testid="hero-o-glow"
        />

        {/* Airliner with towed JODI banner */}
        <motion.div
          className="pointer-events-none absolute left-0 top-0 z-20 h-14 w-44 drop-shadow-[0_22px_30px_rgba(30,60,100,0.4)] sm:h-[70px] sm:w-56"
          style={{
            x: planeX,
            y: planeY,
            rotate: planeRotate,
            scale: planeScale,
            opacity: planeOpacity,
            translateX: "-50%",
            translateY: "-50%",
          }}
          data-testid="hero-airplane"
        >
          {/* Tow rope */}
          <div className="absolute right-[92%] top-[52%] h-[2px] w-14 -translate-y-1/2 bg-white/80" />
          {/* Towed banner */}
          <div
            className="banner-wave absolute right-[104%] top-1/2 flex h-12 w-40 -translate-y-1/2 items-center justify-center shadow-[0_14px_30px_rgba(30,60,100,0.35)] sm:h-14 sm:w-48"
            style={{ background: "linear-gradient(180deg, #3FA9F5 0%, #1E86D9 100%)" }}
            data-testid="hero-tow-banner"
          >
            <span
              className="font-display font-extrabold uppercase text-white"
              style={{
                fontSize: "clamp(1.1rem, 2vw, 1.6rem)",
                letterSpacing: "0.14em",
                WebkitTextStroke: "0.6px rgba(255,255,255,0.9)",
                textShadow: "0 1px 2px rgba(11,60,110,0.45)",
                transform: "scaleX(1.06)",
              }}
            >
              JODI
            </span>
          </div>

          {/* Evening navigation lights */}
          <div
            className={`absolute inset-0 transition-opacity duration-700 ${night ? "opacity-100" : "opacity-0"}`}
            data-testid="hero-plane-lights"
            aria-hidden="true"
          >
            {/* Tail strobe (blinking) */}
            <span
              className="plane-strobe absolute left-[5%] top-[30%] h-1.5 w-1.5 rounded-full bg-white"
              style={{ boxShadow: "0 0 10px 4px rgba(255,255,255,0.9)" }}
            />
            {/* Wingtip red (near wing) */}
            <span
              className="absolute bottom-[6%] left-[26%] h-1.5 w-1.5 rounded-full bg-red-500"
              style={{ boxShadow: "0 0 10px 4px rgba(239,68,68,0.85)" }}
            />
            {/* Wingtip green (far wing) */}
            <span
              className="absolute left-[64%] top-[7%] h-1.5 w-1.5 rounded-full bg-green-400"
              style={{ boxShadow: "0 0 10px 4px rgba(74,222,128,0.85)" }}
            />
            {/* Warm cabin windows */}
            <span
              className="absolute left-[27%] top-[43%] h-[3px] w-[58%]"
              style={{
                backgroundImage: "radial-gradient(circle, #ffd9a0 1.3px, transparent 1.7px)",
                backgroundSize: "9px 3px",
                filter: "drop-shadow(0 0 3px rgba(255,200,130,0.9))",
              }}
            />
            {/* Nose landing light */}
            <span
              className="absolute right-[1%] top-[46%] h-2 w-2 rounded-full bg-amber-100"
              style={{ boxShadow: "0 0 18px 8px rgba(255,240,200,0.95)" }}
            />
          </div>

          <Airliner />
        </motion.div>

        {/* Content */}
        <motion.div
          className="relative z-10 flex flex-col items-center px-5 text-center"
          style={{ opacity: titleOpacity }}
        >
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.5 }}
            className="mb-8 flex items-center gap-3 border border-ink/15 bg-white/60 px-5 py-2 backdrop-blur-md"
            data-testid="hero-badge"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-cobalt" />
            <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-ink/70 sm:text-[11px]">
              Hub Ingrosso Calzature — Napoli, Italia
            </span>
          </motion.div>

          <h1
            className={`whitespace-nowrap font-display font-extrabold leading-[0.85] tracking-tighter transition-colors duration-1000 ${
              night ? "text-white" : "text-ink"
            }`}
            style={{ fontSize: "clamp(2.8rem, 13vw, 12rem)", maxWidth: "100vw", textShadow: night ? "0 4px 60px rgba(20,10,50,0.8)" : "0 4px 60px rgba(255,255,255,0.45)" }}
            data-testid="hero-title"
          >
            <span className="block overflow-hidden pb-[0.08em]">
              <motion.span
                className="inline-block"
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.2, ease: EASE, delay: 0.65 }}
              >
                i<span ref={oRef} className="inline-block">o</span>gama
              </motion.span>
            </span>
          </h1>

          <div className="mt-2 overflow-hidden">
            <motion.p
              initial={{ y: "110%" }}
              animate={{ y: 0 }}
              transition={{ duration: 1, ease: EASE, delay: 0.9 }}
              className={`font-mono text-[11px] uppercase tracking-[0.34em] transition-colors duration-1000 sm:text-xs ${
                night ? "text-white/80" : "text-ink/70"
              }`}
              data-testid="hero-tagline"
            >
              Eccellenza calzaturiera italiana · Solo ingrosso
            </motion.p>
          </div>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 1.15 }}
            className={`mt-7 max-w-xl rounded-sm px-4 py-2 text-sm leading-relaxed backdrop-blur-[2px] transition-colors duration-1000 sm:text-base ${
              night ? "bg-white/10 text-white/85" : "bg-white/25 text-ink/70"
            }`}
            data-testid="hero-subtitle"
          >
            Distribuzione all'ingrosso di calzature dei migliori marchi dal cuore di Napoli.
            Prezzi di ingrosso, lotti a box per colore, spedizione rapida in Europa e nel mondo.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 1.35 }}
            className="mt-10 flex flex-col items-center gap-4 sm:flex-row"
          >
            <button
              onClick={() => scrollToId("#catalogo")}
              className="group flex items-center gap-3 bg-ink px-8 py-4 font-mono text-[11px] uppercase tracking-[0.22em] text-white transition-colors duration-300 hover:bg-cobalt"
              data-testid="hero-cta-explore-catalog"
            >
              Esplora il catalogo
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              onClick={() => scrollToId("#contatti")}
              className="flex items-center gap-3 border border-ink/25 bg-white/60 px-8 py-4 font-mono text-[11px] uppercase tracking-[0.22em] text-ink backdrop-blur-sm transition-colors duration-300 hover:border-ink hover:bg-ink hover:text-white"
              data-testid="hero-cta-contact"
            >
              Contatti diretti
            </button>
          </motion.div>
        </motion.div>

        {/* Day/evening toggle */}
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.2, duration: 1 }}
          onClick={toggleNight}
          className={`absolute bottom-8 left-5 z-30 flex items-center gap-2.5 border px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.24em] backdrop-blur-md transition-colors duration-500 lg:left-8 ${
            night
              ? "border-white/30 bg-white/10 text-white hover:bg-white/20"
              : "border-ink/15 bg-white/60 text-ink hover:bg-white/80"
          }`}
          data-testid="hero-night-toggle"
        >
          {night ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
          {night ? "Giorno" : "Volo serale"}
        </motion.button>

        {/* Scroll cue */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2, duration: 1 }}
          className="absolute bottom-8 z-10 flex flex-col items-center gap-2"
          data-testid="hero-scroll-cue"
        >
          <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}>
            <ArrowDown className={`h-4 w-4 transition-colors duration-1000 ${night ? "text-white/70" : "text-ink/60"}`} />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
